import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const progress = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

const ToastContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '20px',
  right: '20px',
  minWidth: '300px',
  maxWidth: '400px',
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  padding: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: theme.shadows[4],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  zIndex: 1500,
  animation: `${slideIn} 0.3s ease-out`,
  transition: 'opacity 0.3s ease-in, transform 0.3s ease-in, visibility 0.3s ease-in',
  '&.closing': {
    animation: `${slideOut} 0.3s ease-in forwards`,
    visibility: 'hidden',
  },
  visibility: 'visible',
}));

const ProgressBar = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  height: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  animation: `${progress} 5s linear`,
});

interface ToastProps {
  message: string;
  open: boolean;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, open, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsClosing(false);
      const timer = setTimeout(() => {
        setIsClosing(true);
        setTimeout(() => {
          setIsVisible(false);
          onClose();
        }, 300);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [open, onClose]);

  if (!isVisible) return null;

  return (
    <ToastContainer className={isClosing ? 'closing' : ''}>
      <Typography variant="body2">{message}</Typography>
      <IconButton
        size="small"
        color="inherit"
        onClick={() => {
          setIsClosing(true);
          setTimeout(() => {
            setIsVisible(false);
            onClose();
          }, 300);
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <ProgressBar />
    </ToastContainer>
  );
};

export default Toast;
